<script setup lang="ts">
import { FormKit } from '@formkit/vue';

const props = defineProps<{
  idOrder: string;
  autoOpen?: boolean;
}>();

const { eligibility, eligibleProducts, returnReasons, requests, submitting, fetchEligibility, fetchReturnRequests, createReturnRequest } =
  useReturns();

await Promise.all([fetchEligibility(props.idOrder), fetchReturnRequests(props.idOrder)]);

const panelRef = ref<HTMLElement | null>(null);
const formOpen = ref(!!props.autoOpen);
const reason = ref('');
const message = ref('');
const submitted = ref<{ reference: string; status_label: string } | null>(null);

const selectedQuantities = reactive<Record<string, number>>({});

const reasonOptions = computed(() => returnReasons.value.map((r) => ({ value: r.code, label: r.label })));

const selectedProducts = computed(() =>
  Object.entries(selectedQuantities)
    .filter(([, quantity]) => quantity > 0)
    .map(([idOrderDetail, quantity]) => ({ id_order_detail: idOrderDetail, quantity })),
);

const canSubmit = computed(() => !!reason.value && selectedProducts.value.length > 0 && !submitting.value);

const toggleProduct = (idOrderDetail: string, checked: boolean) => {
  selectedQuantities[idOrderDetail] = checked ? 1 : 0;
};

const onSubmit = async () => {
  const ok = await createReturnRequest({
    id_order: props.idOrder,
    reason: reason.value,
    message: message.value,
    products: selectedProducts.value,
  });

  if (ok) {
    const [latest] = requests.value;
    submitted.value = latest ? { reference: latest.reference, status_label: latest.status_label } : null;
    formOpen.value = false;
    reason.value = '';
    message.value = '';
    Object.keys(selectedQuantities).forEach((key) => delete selectedQuantities[key]);
  }
};

onMounted(() => {
  if (props.autoOpen) {
    nextTick(() => panelRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  }
});
</script>

<template>
  <div id="returns" ref="panelRef" class="return-request-panel mt-6 scroll-mt-24 border-t border-gray-200 pt-6">
    <h2 class="text-lg font-medium text-gray-900 mb-4">
      {{ $t('Returns and Refunds') }}
    </h2>

    <div v-if="requests.length" class="mb-6 space-y-3">
      <h3 class="text-sm font-medium text-gray-900">{{ $t('Your return requests') }}</h3>
      <div v-for="request in requests" :key="request.reference" class="rounded-lg border border-gray-200 p-4 text-sm">
        <div class="flex flex-wrap items-center justify-between gap-2">
          <span class="font-medium text-gray-900">{{ request.reference }}</span>
          <span class="text-gray-500">{{ request.status_label }}</span>
        </div>
        <p v-if="request.reason_label" class="mt-1 text-gray-500">{{ request.reason_label }}</p>
        <ul v-if="request.products?.length" class="mt-2 text-gray-500">
          <li v-for="(product, index) in request.products" :key="index">
            {{ product.quantity }} &times; {{ product.product_name }}
          </li>
        </ul>
      </div>
    </div>

    <div v-if="submitted" class="mb-6 rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-800">
      {{ $t('Return request submitted') }} &mdash; {{ submitted.reference }} ({{ submitted.status_label }})
    </div>

    <div v-if="eligibility?.return">
      <button v-if="!formOpen" type="button" class="btn btn-secondary" @click="formOpen = true">
        {{ $t('Ask for refund') }}
      </button>

      <FormKit
        v-else
        type="form"
        :actions="false"
        class="space-y-4 rounded-lg border border-gray-200 p-4"
        @submit="onSubmit"
      >
        <div>
          <p class="mb-2 text-sm font-medium text-gray-900">{{ $t('Order items') }}</p>
          <div
            v-for="product in eligibleProducts"
            :key="product.id_order_detail"
            class="flex flex-wrap items-center gap-3 py-1"
          >
            <input
              :id="`return-product-${product.id_order_detail}`"
              type="checkbox"
              class="h-4 w-4 shrink-0"
              @change="toggleProduct(product.id_order_detail, ($event.target as HTMLInputElement).checked)"
            />
            <label :for="`return-product-${product.id_order_detail}`" class="min-w-0 flex-1 break-words text-sm text-gray-700">
              {{ product.product_name }}
            </label>
            <label class="sr-only" :for="`return-quantity-${product.id_order_detail}`">{{ $t('Quantity to return') }}</label>
            <input
              :id="`return-quantity-${product.id_order_detail}`"
              v-model.number="selectedQuantities[product.id_order_detail]"
              type="number"
              min="1"
              :max="Number(product.product_quantity)"
              :disabled="!selectedQuantities[product.id_order_detail]"
              class="w-16 shrink-0 rounded border border-gray-300 px-2 py-1 text-sm"
            />
          </div>
        </div>

        <FormKit
          v-model="reason"
          type="select"
          name="reason"
          :label="$t('Return reason')"
          :options="reasonOptions"
          validation="required"
        />

        <FormKit v-model="message" type="textarea" name="message" :label="$t('Additional details')" />

        <div class="flex flex-wrap items-center gap-3">
          <button type="submit" class="btn btn-primary" :disabled="!canSubmit">
            {{ $t('Submit return request') }}
          </button>
          <button type="button" class="btn btn-secondary" @click="formOpen = false">
            {{ $t('Cancel') }}
          </button>
        </div>
      </FormKit>
    </div>
  </div>
</template>
